/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import {
    IContentBlockAdditionalContentItemViewProps,
    IContentBlockAdditionalContentViewProps
} from '@msdyn365-commerce-modules/content-block/src/modules/content-block/./components/additional-content';
import { IContentBlockViewProps } from '@msdyn365-commerce-modules/content-block/src/modules/content-block/./content-block';
import { IContentBlockProps as IContentBlockExtensionProps } from '../definition-extensions/content-block.ext.props.autogenerated';

/**
 * Render Additional Content.
 * @param additionalContent - Additional content view props.
 * @returns JSX Element.
 */
const renderAdditionalContent = (additionalContent: IContentBlockAdditionalContentViewProps) => {
    return (
        <Node {...additionalContent.additionalContentNode}>
            {additionalContent.additionalContentItems?.map((item: IContentBlockAdditionalContentItemViewProps) => {
                return (
                    <>
                        {item.heading}
                        <Node {...item.additionalContentItemContainer}>
                            {item.text}
                            <Node {...item.additionalContentItemLinks}>{item.links}</Node>
                        </Node>
                    </>
                );
            })}
        </Node>
    );
};

const ContentBlockView: React.FC<IContentBlockViewProps & IContentBlockExtensionProps<{}>> = props => {
    const {
        contentBlockContainer,
        imageContainer,
        detailsContainer,
        title,
        text,
        links,
        image,
        contentBlockAnchorTag,
        imageLink,
        imageAriaLabel,
        additionalContent,
        config: { logoImage, scrollDownMessage }
    } = props;
    const handleScroll = () => {
        const scrollSection = document.querySelector('.scroll-section');
        if (scrollSection) {
            scrollSection.scrollIntoView({ behavior: 'smooth' });
        }
    };

    if (imageLink) {
        return (
            <Module {...contentBlockContainer}>
                <Node
                    {...contentBlockAnchorTag}
                    href={imageLink}
                    className={contentBlockAnchorTag ? contentBlockAnchorTag.className : ''}
                    aria-label={imageAriaLabel}
                >
                    <Node {...imageContainer}>{image}</Node>
                </Node>
                <Node {...detailsContainer}>
                    {logoImage && <img className='content-block-logo' src={logoImage?.src} alt={logoImage?.altText} />}
                    {title}
                    {text}
                    {links}
                    <button className='scroll-btn' onClick={handleScroll}>
                        <span className='arrow-text'>{scrollDownMessage}</span>
                    </button>
                    {additionalContent && renderAdditionalContent(additionalContent)}
                </Node>
            </Module>
        );
    }
    return (
        <Module {...contentBlockContainer}>
            <Node {...imageContainer}>{image}</Node>
            <Node {...detailsContainer}>
                {logoImage && <img className='content-block-logo' src={logoImage?.src} alt={logoImage?.altText} />}
                {title}
                {text}
                {links}
                <button className='scroll-btn' onClick={handleScroll}>
                    <span className='arrow-text'>{scrollDownMessage}</span>
                </button>
                {additionalContent && renderAdditionalContent(additionalContent)}
            </Node>
        </Module>
    );
};

export default ContentBlockView;
